import React, { useState, useRef, useEffect } from 'react';
import { Input } from './input';
import { LabelDefinition } from '@/types/label-budget';
import { PlusIcon } from './Icons';

interface AddColumnButtonProps {
  onAdd: (name: string, type: LabelDefinition['type'], employeeField?: LabelDefinition['employeeField']) => void;
  disabled?: boolean;
}

// Employee field presets
const EMPLOYEE_FIELD_PRESETS = [
  { field: 'name' as const, label: 'Name', description: 'Employee name' },
  { field: 'email' as const, label: 'Email', description: 'Employee email address' },
  { field: 'department' as const, label: 'Department', description: 'Department/Division' },
  { field: 'role' as const, label: 'Role', description: 'Job title/Position' },
  { field: 'employeeId' as const, label: 'Employee ID', description: 'Employee number' },
  { field: 'phone' as const, label: 'Phone', description: 'Contact number' },
  { field: 'joinDate' as const, label: 'Join Date', description: 'Date of joining' },
];

export const AddColumnButton: React.FC<AddColumnButtonProps> = ({
  onAdd,
  disabled = false
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [labelName, setLabelName] = useState('');
  const [labelType, setLabelType] = useState<LabelDefinition['type']>('text');
  const [selectedPreset, setSelectedPreset] = useState<string>('');
  const [inputMode, setInputMode] = useState<'preset' | 'custom'>('preset');
  const tooltipRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLTableCellElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTooltip]);

  const handleAdd = () => {
    if (inputMode === 'preset' && selectedPreset) {
      const preset = EMPLOYEE_FIELD_PRESETS.find(p => p.field === selectedPreset);
      if (preset) {
        onAdd(preset.label, 'employee', preset.field);
        resetForm();
      }
    } else if (inputMode === 'custom' && labelName.trim()) {
      onAdd(labelName.trim(), labelType);
      resetForm();
    }
  };

  const resetForm = () => {
    setLabelName('');
    setLabelType('text');
    setSelectedPreset('');
    setShowTooltip(false);
    setInputMode('preset');
  };

  const handleCancel = () => {
    resetForm();
  };

  return (
    <th 
      ref={buttonRef}
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider relative"
    >
      <button
        onClick={() => setShowTooltip(!showTooltip)}
        className="w-full h-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
        title="Add new column"
        disabled={disabled}
      >
        <PlusIcon className="w-4 h-4" />
      </button>

      {showTooltip && !disabled && (
        <div
          ref={tooltipRef}
          className="absolute right-0 top-full mt-2 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl p-4 min-w-[280px]"
        >
          <div className="space-y-3">
            {/* Header */}
            <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-600 pb-2">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Add New Column
              </h4>
            </div>

            {/* Tab Selection */}
            <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setInputMode('preset')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  inputMode === 'preset'
                    ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                Employee Info
              </button>
              <button
                onClick={() => setInputMode('custom')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  inputMode === 'custom'
                    ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                Custom Field
              </button>
            </div>

            {/* Form */}
            <div className="space-y-3">
              {inputMode === 'preset' ? (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Employee Field
                    </label>
                    <div className="space-y-1">
                      {EMPLOYEE_FIELD_PRESETS.map((preset) => (
                        <label
                          key={preset.field}
                          className={`flex items-start p-2 rounded cursor-pointer transition-colors ${
                            selectedPreset === preset.field
                              ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent'
                          }`}
                        >
                          <input
                            type="radio"
                            name="preset"
                            value={preset.field}
                            checked={selectedPreset === preset.field}
                            onChange={(e) => setSelectedPreset(e.target.value)}
                            className="mt-0.5 mr-2"
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {preset.label}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {preset.description}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    These fields will be automatically populated from employee mapping data.
                  </p>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Column Name
                    </label>
                    <Input
                      type="text"
                      value={labelName}
                      onChange={(e) => setLabelName(e.target.value)}
                      placeholder="Enter column name"
                      className="w-full text-sm"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && labelName.trim()) {
                          handleAdd();
                        } else if (e.key === 'Escape') {
                          handleCancel();
                        }
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Column Type
                    </label>
                    <select
                      value={labelType}
                      onChange={(e) => setLabelType(e.target.value as LabelDefinition['type'])}
                      className="w-full px-2 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded"
                    >
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="currency">Currency</option>
                      <option value="select">Select (Dropdown)</option>
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {labelType === 'text' && 'For names, labels, or any text'}
                      {labelType === 'number' && 'For numeric values'}
                      {labelType === 'currency' && 'For monetary values with currency symbol'}
                      {labelType === 'select' && 'For predefined options'}
                    </p>
                  </div>
                </>
              )}
              
              <div className="flex gap-2 justify-end pt-2">
                <button
                  onClick={handleCancel}
                  className="px-3 py-1.5 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdd}
                  disabled={inputMode === 'preset' ? !selectedPreset : !labelName.trim()}
                  className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Column
                </button>
              </div>
            </div>

            {/* Info */}
            <div className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-600">
              <p>• Maximum 5 custom columns allowed</p>
              <p>• Drag column headers to reorder</p>
              {inputMode === 'preset' && (
                <p>• Employee data syncs automatically</p>
              )}
            </div>
          </div>
        </div>
      )}
    </th>
  );
};